import { FC, useLayoutEffect, useRef, useState } from "react";

import { Container } from "../../../components/Container/Container";
import { ContentBuilder } from "../../../components/ContentBuilder/ContentBuilder";
import { ContentFooter } from "../../../components/ContentFooter/ContentFooter";
import { PageContentSource } from "../../../config/routes/content";
import { ReactComponent as Download } from "../../../assets/vectors/icons/download.svg";
import { Breadcrumb } from "../../../components/Breadcrumb/Breadcrumb";
import { Seo } from "../../../components/Seo/Seo";
import { usePageContent } from "../../../hooks/content/usePageContent";
import { useRouteInfo } from "../../../hooks/route/useRouteInfo";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { UiBreakpoints } from "../../../config/ui/breakpoints";
import { MarginaliaProvider, useMarginaliaContext } from "../../../components/ContentBuilder/MarginaliaContext/MarginaliaContext";

const AsideContent: FC<{ blocks: ReturnType<typeof usePageContent>["blocks"] }> = ({ blocks }) => {
	const { blockRefs } = useMarginaliaContext();
	const asideRef = useRef<HTMLDivElement>(null);
	const [positions, setPositions] = useState<Record<string, number>>({});

	useLayoutEffect(() => {
		const compute = () => {
			if (!asideRef.current) return;
			const asideRect = asideRef.current.getBoundingClientRect();
			const next: Record<string, number> = {};
			for (const { id, el } of blockRefs) {
				const blockRect = el.getBoundingClientRect();
				next[id] = blockRect.top - asideRect.top;
			}
			setPositions(next);
		};

		compute();

		const observer = new ResizeObserver(compute);
		if (asideRef.current) observer.observe(asideRef.current);
		for (const { el } of blockRefs) observer.observe(el);

		return () => observer.disconnect();
	}, [blockRefs]);

	const marginaliaBlocks = blocks.filter((block) => block.marginalia);

	return (
		<div ref={asideRef} className="relative w-full">
			{marginaliaBlocks.map((block) => {
				const MarginaliaComponent = block.marginalia!.component;
				const top = positions[block.id];
				return (
					<div
						key={block.id}
						style={{ position: "absolute", top: top ?? 0, left: 0, right: 0 }}
					>
						<MarginaliaComponent {...(block.marginalia!.props ?? {})} />
					</div>
				);
			})}
		</div>
	);
};

const PillarDetail: FC = () => {
	const { routeData, title, description, routeParent } = useRouteInfo();
	const { width } = useWindowSize();
	const sidebarCollapsed = width < UiBreakpoints.lg;

	const { blocks, pageIntro, source, pdf, pageMarginalia } = usePageContent();

	const hasSourceUrlArray = Array.isArray(source);
	const hasSource = typeof source === "object" && !hasSourceUrlArray && typeof (source as PageContentSource).label === "string";
	const hasSourceUrl = typeof source === "object" && typeof (source as PageContentSource)?.url === "string";

	return (
		<MarginaliaProvider inline={sidebarCollapsed}>
			<>
				<Seo
					title={title}
					description={typeof description === "string" && description.trim().length > 0 ? description : undefined}
				/>

				<div className="w-full flex items-center p-6 md:px-9 xl:px-14 bg-white border-b border-gray-200">
					<Breadcrumb
						items={[
							{ label: "Home", to: "/", className: "text-secondary" },
							{ label: `Pillar ${routeParent?.pillarNumber}: ${routeParent?.label ?? ""}`, to: `/pillar/${routeParent?.pillarNumber}`, className: "text-gray-400" },
							{ label: routeData?.label ?? "", className: "text-gray-800" },
						]}
					/>
				</div>

				<article className="break-words flex">
					<div className="w-full min-w-0" style={{ maxWidth: 1020 }}>
						<Container contentPaddingClasses="px-6 pt-8 pb-0 md:px-9 md:pt-9 xl:px-14 xl:pt-14">
							<div className="flex flex-col gap-1 w-full max-w-6xl mx-auto">
								<p className="text-primary uppercase font-light tracking-wide">
									Pillar {routeParent?.pillarNumber}: {routeParent?.label ?? ""}
								</p>
								<h1 className="text-3xl font-black text-black">{routeData?.label ?? ""}</h1>
								{typeof pdf !== "undefined" && (
									<a
										className="flex items-center gap-2 text-primary text-sm hover:underline hover:text-primary-hover"
										href={`${window.location.origin}${pdf?.path}`}
										download={pdf.filename ? pdf.filename : true}
									>
										<Download width={18} />
										Download this chart as PDF
									</a>
								)}
							</div>
						</Container>

						<Container contentClassName="!py-0">
							<div className="w-full max-w-6xl mx-auto">
								{typeof pageIntro !== "undefined" ? <p className="text-xl leading-relaxed">{pageIntro}</p> : null}
								<ContentBuilder blocks={blocks} />
								{hasSource && !hasSourceUrlArray && (
									<div className="flex mt-2">
										<p className="text-gray-500 italic text-sm">
											Source:{" "}
											{hasSourceUrl ? (
												<a target="_blank" href={source.url} rel="noreferrer" className="text-primary hover:text-primary-hover hover:underline">
													{source.label}
												</a>
											) : (
												source.label
											)}
										</p>
									</div>
								)}
								{hasSourceUrlArray && (
									<div className="flex mt-2">
										<p className="text-gray-500 italic text-sm">
											Source:{" "}
											{source.map((sourceChild, idx: number) => (
												<span key={sourceChild.url}>
													<a
														target="_blank"
														href={sourceChild.url}
														rel="noreferrer"
														className="text-primary hover:text-primary-hover hover:underline"
													>
														{sourceChild.label}
													</a>
													{idx + 1 !== source.length && <span>, </span>}
												</span>
											))}
										</p>
									</div>
								)}
								<ContentFooter />
							</div>
						</Container>
					</div>

					{!sidebarCollapsed && (
						<aside style={{ flex: 1, minWidth: 240 }} className="bg-gray-100">
							<div className="p-6" style={{ maxWidth: 450 }}>
								{pageMarginalia && (() => {
									const PageMarginaliaComponent = pageMarginalia.component;
									return (
										<div className="mb-8">
											<PageMarginaliaComponent {...(pageMarginalia.props ?? {})} />
										</div>
									);
								})()}
								<AsideContent blocks={blocks} />
							</div>
						</aside>
					)}
				</article>
			</>
		</MarginaliaProvider>
	);
};

export { PillarDetail };
